import { ExternalLink } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import type { AnswerOut, CadeRelatorNarrative, CadeSeiLink } from '@/services/types';

const VOCABULARY: Record<string, string> = {
  conduct_type: 'conduct_type',
  theory_of_harm: 'theory_of_harm',
};

/**
 * A relator's record told as cases, not rates (analytics/cade/relator_profile.py): theses read
 * from the votes of the cases they reported, vista requests, fines in their decisions, and split
 * decisions with every member the minutes name. Every row leads to its case, and to the vote in
 * SEI where one is placed under that session. Each block is a `data-panel` so a person report
 * can cite it.
 */
export function RelatorNarrative({ narrative }: { narrative: AnswerOut<CadeRelatorNarrative> }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);
  return (
    <section className="space-y-4 rounded-lg border bg-card p-4" data-panel="relator-narrative">
      <h2 className="text-sm font-semibold text-foreground">{t('cade:narrative.title')}</h2>
      <p className="text-xs text-muted-foreground">{t('cade:narrative.help')}</p>
      <Answer answer={narrative} scope="record" help={t('cade:narrative.help')}>
        {(value: CadeRelatorNarrative) => (
          <div className="space-y-5 text-sm">
            <p className="text-xs text-muted-foreground" data-narrative-summary>
              {value.coded_cases === null
                ? t('cade:narrative.summaryUncoded', {
                    decisions: number.format(value.reported_decisions),
                    cases: number.format(value.reported_cases),
                  })
                : t('cade:narrative.summary', {
                    decisions: number.format(value.reported_decisions),
                    cases: number.format(value.reported_cases),
                    coded: number.format(value.coded_cases),
                  })}
            </p>
            <Theses value={value} />
            <Vistas value={value} />
            <Fines value={value} />
            <Splits value={value} />
          </div>
        )}
      </Answer>
    </section>
  );
}

function Block({
  panel,
  title,
  note,
  empty,
  count,
  children,
}: {
  panel: string;
  title: string;
  note?: string;
  empty: string;
  count: number;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2" data-panel={panel}>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title} ({count})
      </h3>
      {note ? <p className="text-xs text-muted-foreground">{note}</p> : null}
      {count === 0 ? <p className="text-xs text-muted-foreground">{empty}</p> : children}
    </div>
  );
}

function CaseLink({ caseKey, caseNumber }: { caseKey: string; caseNumber: string }) {
  return (
    <Link
      to={`/cade/c/${caseKey}`}
      className="tabular-nums text-primary underline-offset-4 hover:underline"
    >
      {caseNumber}
    </Link>
  );
}

function VoteLinks({ votes }: { votes: CadeSeiLink[] }) {
  const { t } = useTranslation(['cade']);
  return (
    <>
      {votes.map((vote) => (
        <a
          key={vote.url}
          href={vote.url}
          target="_blank"
          rel="noreferrer"
          className="ml-2 inline-flex items-center gap-1 text-xs text-primary underline-offset-4 hover:underline"
        >
          {t('cade:narrative.vote')}
          {vote.doc_date ? ` (${vote.doc_date})` : ''}
          <ExternalLink className="h-3 w-3" aria-hidden />
        </a>
      ))}
    </>
  );
}

function Theses({ value }: { value: CadeRelatorNarrative }) {
  const { t } = useTranslation(['cade']);
  return (
    <Block
      panel="relator-narrative-theses"
      title={t('cade:narrative.theses.title')}
      note={t('cade:narrative.theses.note')}
      empty={t('cade:narrative.theses.empty')}
      count={value.theses.length}
    >
      <ul className="space-y-2">
        {value.theses.map((thesis) => (
          <li key={`${thesis.field}-${thesis.code}`}>
            <details>
              <summary className="cursor-pointer text-foreground">
                <span className="text-xs text-muted-foreground">
                  {t(`cade:codings.fields.${thesis.field}`, { defaultValue: thesis.field })}:
                </span>{' '}
                {t(`cade:codings.values.${VOCABULARY[thesis.field] ?? thesis.field}.${thesis.code}`, {
                  defaultValue: thesis.code,
                })}{' '}
                <span className="tabular-nums text-muted-foreground">({thesis.cases.length})</span>
              </summary>
              <ul className="mt-1 space-y-1 pl-4">
                {thesis.cases.map((c) => (
                  <li key={c.case_number}>
                    <CaseLink caseKey={c.case_key} caseNumber={c.case_number} />
                    {c.quote ? (
                      <blockquote className="mt-0.5 border-l-2 pl-2 text-xs text-muted-foreground">
                        {c.quote}
                      </blockquote>
                    ) : null}
                  </li>
                ))}
              </ul>
            </details>
          </li>
        ))}
      </ul>
    </Block>
  );
}

function Vistas({ value }: { value: CadeRelatorNarrative }) {
  const { t } = useTranslation(['cade']);
  return (
    <Block
      panel="relator-narrative-vistas"
      title={t('cade:narrative.vistas.title')}
      empty={t('cade:narrative.vistas.empty')}
      count={value.vistas.length}
    >
      <ul className="space-y-1">
        {value.vistas.map((v, index) => (
          <li key={`${v.case_number}-${index}`} className="flex flex-wrap gap-x-2">
            <CaseLink caseKey={v.case_key} caseNumber={v.case_number} />
            <span className="text-xs text-muted-foreground">{v.session_date ?? '—'}</span>
            <span className="text-xs text-foreground">
              {v.days === null
                ? t('cade:narrative.vistas.open')
                : t('cade:narrative.vistas.days', { days: v.days })}
            </span>
            {v.next_outcomes.length > 0 ? (
              <span className="text-xs text-muted-foreground">
                {v.next_outcomes
                  .map((code) => t(`cade:overview.outcomes.${code}`, { defaultValue: code }))
                  .join('; ')}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </Block>
  );
}

function Fines({ value }: { value: CadeRelatorNarrative }) {
  const { t, i18n } = useTranslation(['cade']);
  const currency = new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'BRL' });
  return (
    <Block
      panel="relator-narrative-fines"
      title={t('cade:narrative.fines.title')}
      note={t('cade:narrative.fines.note')}
      empty={t('cade:narrative.fines.empty')}
      count={value.fines.length}
    >
      <ul className="space-y-2">
        {value.fines.map((f) => (
          <li key={f.decision_key} className="space-y-0.5">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <CaseLink caseKey={f.case_key} caseNumber={f.case_number} />
              <span className="text-xs text-muted-foreground">{f.session_year ?? '—'}</span>
              {f.conduct ? (
                <span className="text-xs text-foreground">
                  {t(`cade:codings.values.conduct_type.${f.conduct}`, { defaultValue: f.conduct })}
                </span>
              ) : null}
              <VoteLinks votes={f.votes} />
            </div>
            <p className="tabular-nums text-xs text-foreground">
              {f.amounts.map((amount) => currency.format(amount)).join(' · ')}
            </p>
            {f.fine_base || f.fine_rates.length > 0 ? (
              <p className="text-xs text-muted-foreground">
                {f.fine_base ? `${t('cade:narrative.fines.base')}: ${f.fine_base}` : ''}
                {f.fine_base && f.fine_rates.length > 0 ? ' · ' : ''}
                {f.fine_rates.length > 0
                  ? `${t('cade:narrative.fines.rate')}: ${f.fine_rates.join('; ')}`
                  : ''}
              </p>
            ) : null}
          </li>
        ))}
      </ul>
    </Block>
  );
}

function Splits({ value }: { value: CadeRelatorNarrative }) {
  const { t } = useTranslation(['cade']);
  return (
    <Block
      panel="relator-narrative-splits"
      title={t('cade:narrative.splits.title')}
      note={t('cade:narrative.splits.note')}
      empty={t('cade:narrative.splits.empty')}
      count={value.splits.length}
    >
      <ul className="space-y-2">
        {value.splits.map((s) => (
          <li key={s.decision_key} className="space-y-0.5">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <CaseLink caseKey={s.case_key} caseNumber={s.case_number} />
              <Link
                to={`/cade/votes/d/${s.decision_key}`}
                className="text-xs text-primary underline-offset-4 hover:underline"
              >
                {s.session_year ?? '—'}
              </Link>
              <span className="text-xs text-muted-foreground">
                {s.outcome_codes
                  .map((code) => t(`cade:overview.outcomes.${code}`, { defaultValue: code }))
                  .join('; ')}
              </span>
              <VoteLinks votes={s.votes} />
            </div>
            {s.positions.length > 0 ? (
              <ul className="flex flex-wrap gap-x-3 text-xs">
                {s.positions.map((p) => (
                  <li key={p.name}>
                    <span className="text-foreground">{p.name}</span>{' '}
                    <span className="text-muted-foreground">
                      {t(`cade:overview.positions.${p.position}`, { defaultValue: p.position })}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-muted-foreground">{t('cade:narrative.splits.noneNamed')}</p>
            )}
          </li>
        ))}
      </ul>
    </Block>
  );
}
